import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress, Tabs, Tab } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import frLocale from "date-fns/locale/fr";
import { ChartBarIcon, ArrowsClockwiseIcon, Eye, EyeSlash } from "@phosphor-icons/react";
import { AuthContext } from "../../contexts";
import { CustomPageHeader, CustomTag, CustomList } from "../../ui";
import axios from "axios";
import { toast } from "react-toastify";
import { toastOptions } from "../../assets/constants";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import AlertOrangeIcon from "../../assets/icons/alert--orange.svg";

const AccountingDashboard = () => {
    const { token } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [isLoaded, setIsLoaded] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const [projectsData, setProjectsData] = useState({
        totalActiveProjects: 0,
        enCoursCount: 0,
        nonCommenceCount: 0,
        totalBudget: 0,
    });
    const [detailedProjects, setDetailedProjects] = useState([]);
    const [recurringData, setRecurringData] = useState({
        mrr: 0,
        activeSubscriptions: 0,
        upcomingSubscriptions: 0,
        totalCustomers: 0,
        mrrByCategory: [],
        categoryAmounts: [],
        financements: [],
    });
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [isSimplifiedView, setIsSimplifiedView] = useState(false);

    // Ajouter dans les useState existants
    const [chartData, setChartData] = useState([]);
    const [selectedMetrics, setSelectedMetrics] = useState(["mrr"]);
    const [chartDateRange, setChartDateRange] = useState({
        startDate: "",
        endDate: "",
    });

    const tabs = [
        { value: "recurrent", label: "Récurrent" },
        { value: "projets", label: "Projets" },
    ];

    const tabRoutes = useMemo(() => tabs.map((tab) => tab.value), []);

    useEffect(() => {
        const currentPath = location.pathname;
        const pathParts = currentPath.split("/");
        const tabFromUrl = pathParts[pathParts.length - 1];

        if (tabRoutes.includes(tabFromUrl)) {
            const tabIndex = tabRoutes.indexOf(tabFromUrl);
            setSelectedTab(tabIndex);
        } else {
            const defaultTab = tabRoutes[0];
            navigate(`/admin/accounting/dashboard/${defaultTab}`, { replace: true });
        }
    }, [location.pathname]);

    const handleTabChange = useCallback(
        (event, newValue) => {
            setSelectedTab(newValue);
            const tabRoute = tabRoutes[newValue];
            navigate(`/admin/accounting/dashboard/${tabRoute}`, { replace: true });
        },
        [tabRoutes, navigate]
    );

    // Ajouter cette fonction après fetchRecurringData
    const fetchChartData = useCallback(async () => {
        try {
            let url = `${process.env.REACT_APP_UPA_API_HOST}pennylane/logs/chart`;

            // Ajouter les paramètres de date si spécifiés
            const params = new URLSearchParams();
            if (chartDateRange.startDate) params.append("startDate", chartDateRange.startDate);
            if (chartDateRange.endDate) params.append("endDate", chartDateRange.endDate);

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setChartData(response.data.data.chartData || []);
        } catch (error) {
            console.error("Erreur lors du chargement des données de chart:", error);
            setChartData([]);
        }
    }, [token, chartDateRange.startDate, chartDateRange.endDate]);

    // Ajouter avant le render
    const availableMetrics = [
        { key: "mrr", label: "MRR", color: "#28a745" },
        { key: "monthlyFinancing", label: "Financement mensuel", color: "#6f42c1" },
        { key: "activeSubscriptions", label: "Abonnements actifs", color: "#007bff" },
        { key: "upcomingSubscriptions", label: "Abonnements à venir", color: "#ffc107" },
        { key: "totalCustomers", label: "Total clients", color: "#17a2b8" },
    ];

    const fetchProjectsData = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}dashboard/projects-complete`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setProjectsData(response.data.metrics);
            setDetailedProjects(response.data.projects);

            // Récupérer la dernière date de mise à jour (on prend le projet le plus récemment mis à jour)
            if (response.data.projects.length > 0) {
                const latestProject = response.data.projects.reduce((latest, project) => {
                    return !latest || (project.updatedAt && new Date(project.updatedAt) > new Date(latest.updatedAt)) ? project : latest;
                }, null);
                setLastUpdatedAt(latestProject?.updatedAt || null);
            }
        } catch (error) {
            console.error("Erreur lors du chargement des données projets:", error);
        }
    }, [token]);

    const syncAsanaData = async () => {
        setIsSyncing(true);

        try {
            await toast.promise(
                axios.get(`${process.env.REACT_APP_UPA_API_HOST}asana/sync`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                {
                    pending: "Synchronisation des projets Asana en cours...",
                    success: "Synchronisation des projets Asana terminée",
                    error: "Erreur lors de la synchronisation Asana",
                },
                toastOptions
            );

            // Recharger les données après la synchronisation
            if (selectedTab === 1) {
                // Onglet Projets
                await fetchProjectsData();
            }
        } catch (error) {
            console.error("Erreur lors de la synchronisation:", error);
        } finally {
            setIsSyncing(false);
        }
    };

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    const fetchRecurringData = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}pennylane/recurring-dashboard`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRecurringData(response.data);
        } catch (error) {
            console.error("Erreur lors du chargement des données récurrentes:", error);
        }
    }, [token]);

    useEffect(() => {
        if (selectedTab === 0) {
            fetchRecurringData();
            fetchChartData();
        } else if (selectedTab === 1) {
            fetchProjectsData();
        }
    }, [selectedTab, fetchRecurringData, fetchProjectsData, fetchChartData]);

    const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d", "#ffc658", "#ff7c7c", "#8dd1e1", "#d084d0"];

    const customTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="custom-dashboard-tooltip">
                    <p className="tooltip-title">{data.name}</p>
                    <p className="tooltip-value">{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(data.value)}</p>
                    <p className="tooltip-percentage">{data.percentage}% du total</p>
                </div>
            );
        }
        return null;
    };

    // Colonnes pour la liste des financements
    const financementColumns = React.useMemo(
        () => [
            {
                Header: "Client",
                accessor: "customer",
                icon: "user",
                Cell: ({ cell: { value } }) => <span>{value || "Client inconnu"}</span>,
            },
            {
                Header: "Label",
                accessor: "label",
                icon: "tag",
                Cell: ({ cell: { value } }) => <span>{value || "Label inconnu"}</span>,
            },
            {
                Header: "Mensualité",
                accessor: "monthlyAmount",
                icon: "currency-eur",
                Cell: ({ cell: { value } }) => (
                    <CustomTag
                        value={`${new Intl.NumberFormat("fr-FR", {
                            style: "currency",
                            currency: "EUR",
                        }).format(value || 0)}`}
                        className="orange"
                    />
                ),
            },
            {
                Header: "Montant total",
                accessor: "totalAmount",
                icon: "currency-eur",
                Cell: ({ cell: { value } }) => (
                    <CustomTag
                        value={`${new Intl.NumberFormat("fr-FR", {
                            style: "currency",
                            currency: "EUR",
                        }).format(value || 0)}`}
                        className="blue"
                    />
                ),
            },
            {
                Header: "Durée",
                accessor: "durationMonths",
                icon: "calendar",
                Cell: ({ cell: { value } }) => <CustomTag value={`${value || 0} mois`} className="primary" />,
            },
            {
                Header: "Période",
                accessor: "startDate",
                icon: "calendar-check",
                Cell: ({ row: { original } }) => {
                    const startDate = original.startDate ? new Date(original.startDate).toLocaleDateString("fr-FR", { month: "short", year: "numeric" }) : "";
                    const finishDate = original.finishDate ? new Date(original.finishDate).toLocaleDateString("fr-FR", { month: "short", year: "numeric" }) : "";

                    if (startDate && finishDate) {
                        return (
                            <span className="period-display">
                                {startDate} → {finishDate}
                            </span>
                        );
                    }
                    return <span className="period-undefined">Non définie</span>;
                },
            },
            {
                Header: "Statut",
                accessor: "status",
                icon: "info",
                Cell: ({ cell: { value } }) => (
                    <CustomTag
                        value={value === "in_progress" ? "En cours" : value === "not_started" ? "À venir" : value}
                        className={value === "in_progress" ? "success" : value === "not_started" ? "warning" : "default"}
                    />
                ),
            },
        ],
        []
    );

    const renderProjectsDashboard = () => {
        return (
            <div className="dashboard-container">
                {/* Première ligne - Cartes de statistiques projets */}
                <div className={`dashboard-grid ${isSimplifiedView ? "dashboard-grid--2-cols" : "dashboard-grid--3-cols"}`}>
                    <div className="dashboard-card">
                        <h3 className="dashboard-card__title">Projets en cours</h3>
                        <div className="dashboard-card__value dashboard-card__value--success">{projectsData.enCoursCount}</div>
                    </div>

                    <div className="dashboard-card">
                        <h3 className="dashboard-card__title">Projets non commencés</h3>
                        <div className="dashboard-card__value dashboard-card__value--warning">{projectsData.nonCommenceCount}</div>
                    </div>

                    {!isSimplifiedView && (
                        <div className="dashboard-card">
                            <h3 className="dashboard-card__title">Budget total</h3>
                            <div className="dashboard-card__value dashboard-card__value--primary">
                                {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(projectsData.totalBudget)}
                            </div>
                        </div>
                    )}
                </div>

                {/* Deuxième ligne - Liste des projets */}
                <div>
                    {detailedProjects.length > 0 ? (
                        <CustomList
                            entity={detailedProjects}
                            columns={[
                                {
                                    Header: "Nom du projet",
                                    accessor: "name",
                                    icon: "id",
                                    Cell: ({ cell: { value } }) => <span className="project-name">{value}</span>,
                                },
                                {
                                    Header: "Statut",
                                    accessor: "status",
                                    icon: "tag-simple",
                                    Cell: ({ cell: { value } }) => <CustomTag className={value === "En cours" ? "success" : "warning"} value={value} />,
                                },
                                {
                                    Header: "Phase",
                                    accessor: "phase",
                                    icon: "calendar",
                                    Cell: ({ cell: { value } }) => <CustomTag className="info" value={value || "Non définie"} />,
                                },
                                ...(isSimplifiedView
                                    ? []
                                    : [
                                          {
                                              Header: "Budget",
                                              accessor: "budget",
                                              icon: "euro",
                                              Cell: ({ cell: { value } }) => {
                                                  const budgetPercentage = projectsData.totalBudget > 0 ? ((value || 0) / projectsData.totalBudget) * 100 : 0;

                                                  return (
                                                      <div className="budget-column">
                                                          <div className="budget-row">
                                                              <span className={`budget-amount ${!value || value === 0 ? "budget-amount--warning" : "budget-amount--normal"}`}>
                                                                  {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value || 0)}
                                                              </span>
                                                              {(!value || value === 0) && <img src={AlertOrangeIcon} alt="Warning" className="budget-warning-icon" />}
                                                          </div>
                                                          <div className="budget-percentage">{budgetPercentage.toFixed(1)}% du total</div>
                                                      </div>
                                                  );
                                              },
                                          },
                                      ]),
                                ...(isSimplifiedView
                                    ? []
                                    : [
                                          {
                                              Header: "Consommé",
                                              accessor: "consumed",
                                              icon: "euro",
                                              Cell: ({ cell: { value }, row: { original } }) => (
                                                  <div className="consumed-column">
                                                      <span className="consumed-amount">{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value || 0)}</span>
                                                      <div className="consumed-days">{original.consumedDays ? `${original.consumedDays.toFixed(1)}j` : "0j"}</div>
                                                  </div>
                                              ),
                                          },
                                      ]),
                                {
                                    Header: "Marge",
                                    accessor: "margin",
                                    icon: "percentage",
                                    Cell: ({ cell: { value }, row: { original } }) => {
                                        const budget = original.budget || 0;
                                        const marginPercentage = budget > 0 ? (value / budget) * 100 : 0;

                                        // Déterminer la couleur selon les tranches de marge
                                        let color = "#dc3545"; // Rouge par défaut (< 20%)
                                        if (marginPercentage >= 60) {
                                            color = "#28a745"; // Vert (60-100%)
                                        } else if (marginPercentage >= 40) {
                                            color = "#ffc107"; // Jaune (40-60%)
                                        } else if (marginPercentage >= 20) {
                                            color = "#f57c00"; // Orange (20-40%)
                                        }

                                        return (
                                            <div className="margin-column">
                                                <span
                                                    className="margin-percentage"
                                                    style={{
                                                        color: color,
                                                    }}
                                                >
                                                    {marginPercentage.toFixed(1)}%
                                                </span>
                                                {!isSimplifiedView && <div className="margin-amount">{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value || 0)}</div>}
                                            </div>
                                        );
                                    },
                                },
                            ]}
                            isFetchable={false}
                            isClickable={false}
                            isSelectable={false}
                        />
                    ) : (
                        <div className="no-projects-message">Aucun projet en cours ou pas commencé</div>
                    )}
                </div>
            </div>
        );
    };

    const renderRecurringDashboard = () => {
        return (
            <div className="dashboard-container">
                {/* Première ligne - 4 cartes 1/4 chacune */}
                <div className="dashboard-grid dashboard-grid--4-cols">
                    <div className="dashboard-card">
                        <h3 className="dashboard-card__title">MRR</h3>
                        <div className="dashboard-card__value dashboard-card__value--success">{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(recurringData.mrr)}</div>
                    </div>

                    <div className="dashboard-card">
                        <h3 className="dashboard-card__title">Financement mensuel</h3>
                        <div className="dashboard-card__value dashboard-card__value--purple">
                            {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(
                                recurringData.financements ? recurringData.financements.reduce((sum, financement) => sum + (financement.monthlyAmount || 0), 0) : 0
                            )}
                        </div>
                    </div>

                    <div className="dashboard-card">
                        <h3 className="dashboard-card__title">Abonnements actifs</h3>
                        <div className="dashboard-card__value dashboard-card__value--primary">{recurringData.activeSubscriptions}</div>
                    </div>

                    <div className="dashboard-card">
                        <h3 className="dashboard-card__title">Abonnements à venir</h3>
                        <div className="dashboard-card__value dashboard-card__value--warning">{recurringData.upcomingSubscriptions}</div>
                    </div>
                </div>

                {/* Deuxième ligne - Graphique donuts + Liste 1/2 chacun */}
                <div className="dashboard-grid dashboard-grid--2-cols">
                    <div className="dashboard-card chart-container">
                        <h3 className="chart-container__title">Répartition MRR par catégorie</h3>
                        <div className="chart-content">
                            {recurringData.mrrByCategory.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={recurringData.mrrByCategory} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={0} dataKey="value">
                                            {recurringData.mrrByCategory.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={customTooltip} />
                                        <Legend wrapperStyle={{ fontSize: "12px" }} formatter={(value, entry) => `${value} (${entry.payload.percentage}%)`} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="chart-no-data">Aucune donnée disponible</div>
                            )}
                        </div>
                    </div>

                    <div className="scrollable-container">
                        <div className="scrollable-content">
                            <style>
                                {`
                                .amounts-list-manual::-webkit-scrollbar {
                                    display: none;
                                }
                                `}
                            </style>
                            <div className="amounts-list-manual">
                                <table className="manual-table">
                                    <thead className="manual-table__header">
                                        <tr>
                                            <th className="manual-table__header-cell">Catégorie</th>
                                            <th className="manual-table__header-cell">Montant récurrent</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recurringData.categoryAmounts.map((category, index) => (
                                            <tr key={index} className="manual-table__row">
                                                <td className="manual-table__cell">
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                        <div
                                                            className="manual-table__color-indicator"
                                                            style={{
                                                                backgroundColor: COLORS[index % COLORS.length],
                                                            }}
                                                        ></div>
                                                        <span>{category.name}</span>
                                                    </div>
                                                </td>
                                                <td className="manual-table__cell">
                                                    <CustomTag
                                                        value={`${new Intl.NumberFormat("fr-FR", {
                                                            style: "currency",
                                                            currency: "EUR",
                                                        }).format(category.amount)}`}
                                                        className="success"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Troisième ligne - Chart linéaire (2/3) + Paramètres (1/3) */}
                <div className="chart-line-section">
                    <div className="dashboard-grid dashboard-grid--2-1">
                        {/* Card Chart - 2/3 */}
                        <div className="dashboard-card chart-container chart-container--large">
                            <h3 className="chart-container__title">Évolution des métriques</h3>

                            <div className="chart-content">
                                {chartData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="date"
                                                stroke="#666"
                                                fontSize={12}
                                                tickFormatter={(value) =>
                                                    new Date(value).toLocaleDateString("fr-FR", {
                                                        day: "2-digit",
                                                        month: "2-digit",
                                                    })
                                                }
                                            />
                                            <YAxis
                                                stroke="#666"
                                                fontSize={12}
                                                domain={[(dataMin) => dataMin * 0.99, (dataMax) => dataMax * 1.01]}
                                                tickFormatter={(value) => Math.round(value).toString()}
                                            />
                                            <Tooltip
                                                contentStyle={{ fontSize: "12px" }}
                                                wrapperClassName="chart-tooltip"
                                                labelFormatter={(value) => new Date(value).toLocaleDateString("fr-FR")}
                                                formatter={(value, name) => {
                                                    const metric = availableMetrics.find((m) => m.key === name);
                                                    if (metric && (name === "mrr" || name === "monthlyFinancing")) {
                                                        return [new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value), metric.label];
                                                    }
                                                    return [value, metric?.label || name];
                                                }}
                                            />
                                            {selectedMetrics.map((metricKey) => {
                                                const metric = availableMetrics.find((m) => m.key === metricKey);
                                                return metric ? (
                                                    <Line
                                                        key={metricKey}
                                                        type="monotone"
                                                        dataKey={metricKey}
                                                        stroke={metric.color}
                                                        strokeWidth={2}
                                                        dot={{ fill: metric.color, strokeWidth: 2, r: 4 }}
                                                        activeDot={{ r: 6 }}
                                                    />
                                                ) : null;
                                            })}
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="chart-no-data">Aucune donnée disponible pour la période sélectionnée</div>
                                )}
                            </div>
                        </div>

                        {/* Card Paramètres - 1/3 */}
                        <div className="parameters-panel">
                            {/* Contrôles de dates */}
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                <h4 style={{ margin: "0", color: "#495057", fontSize: "14px", fontWeight: "600" }}>Période</h4>
                                {/* Ligne avec les deux DatePickers */}
                                <div style={{ display: "flex", gap: "10px" }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <label className="date" style={{ fontSize: "12px", color: "#6c757d", marginBottom: "6px", display: "block" }}>
                                            Date de début
                                        </label>
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
                                            <DatePicker
                                                value={chartDateRange.startDate ? dayjs(chartDateRange.startDate) : null}
                                                onChange={(newValue) =>
                                                    setChartDateRange((prev) => ({
                                                        ...prev,
                                                        startDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                                                    }))
                                                }
                                                format="DD/MM/YYYY"
                                                slotProps={{
                                                    textField: {
                                                        placeholder: "Sélectionner une date",
                                                        InputLabelProps: {
                                                            shrink: false,
                                                        },
                                                        sx: {
                                                            width: "100%",
                                                            "& .MuiInputBase-root": { height: "40px" },
                                                            "& .MuiOutlinedInput-root": { height: "40px" },
                                                            "& .MuiInputBase-input": {
                                                                cursor: "pointer",
                                                                fontSize: "12px",
                                                            },
                                                            "& .MuiInputBase-input::placeholder": {
                                                                opacity: 1,
                                                                color: "#999",
                                                            },
                                                        },
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <label className="date" style={{ fontSize: "12px", color: "#6c757d", marginBottom: "6px", display: "block" }}>
                                            Date de fin
                                        </label>
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
                                            <DatePicker
                                                value={chartDateRange.endDate ? dayjs(chartDateRange.endDate) : null}
                                                onChange={(newValue) =>
                                                    setChartDateRange((prev) => ({
                                                        ...prev,
                                                        endDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                                                    }))
                                                }
                                                format="DD/MM/YYYY"
                                                minDate={chartDateRange.startDate ? dayjs(chartDateRange.startDate) : undefined}
                                                slotProps={{
                                                    textField: {
                                                        placeholder: "",
                                                        sx: {
                                                            width: "100%",
                                                            "& .MuiInputBase-root": { height: "40px" },
                                                            "& .MuiOutlinedInput-root": { height: "40px" },
                                                            "& .MuiInputBase-input": {
                                                                cursor: "pointer",
                                                                fontSize: "12px",
                                                            },
                                                        },
                                                        inputProps: {
                                                            placeholder: "Sélectionner une date",
                                                        },
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                </div>
                                {/* Bouton Réinitialiser en dessous */}
                                <button
                                    onClick={() => setChartDateRange({ startDate: "", endDate: "" })}
                                    style={{
                                        padding: "8px 12px",
                                        backgroundColor: "#fff",
                                        border: "1px solid #ddd",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                        cursor: "pointer",
                                    }}
                                >
                                    Réinitialiser
                                </button>
                            </div>

                            {/* Sélecteur de métriques */}
                            <div className="parameters-section" style={{ flex: 1 }}>
                                <h4 className="parameters-section__subtitle">Métriques affichées</h4>
                                <div className="metrics-grid">
                                    {availableMetrics.map((metric) => (
                                        <label
                                            key={metric.key}
                                            className={`metric-checkbox ${selectedMetrics.includes(metric.key) ? "metric-checkbox--selected" : ""}`}
                                            style={{
                                                backgroundColor: selectedMetrics.includes(metric.key) ? `${metric.color}15` : undefined,
                                                borderColor: selectedMetrics.includes(metric.key) ? metric.color : undefined,
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedMetrics.includes(metric.key)}
                                                onChange={() => {
                                                    setSelectedMetrics([metric.key]);
                                                }}
                                                className="metric-checkbox__input"
                                            />
                                            <div
                                                className="metric-checkbox__indicator"
                                                style={{
                                                    backgroundColor: metric.color,
                                                }}
                                            ></div>
                                            <span className="metric-checkbox__label">{metric.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quatrième ligne - Liste des financements */}
                {recurringData.financements && recurringData.financements.length > 0 && (
                    <div className="financing-section">
                        <div className="financing-header">
                            <h3 className="chart-container__title">Financements en cours ({recurringData.financements.length})</h3>
                        </div>
                        <CustomList
                            entity={recurringData.financements}
                            columns={financementColumns}
                            showSearch={false}
                            isSelectable={false}
                            isEditable={false}
                            isFetchable={false}
                            isClickable={false}
                        />
                    </div>
                )}
            </div>
        );
    };

    const renderTabContent = () => {
        switch (selectedTab) {
            case 0: // Récurrent
                return renderRecurringDashboard();
            case 1: // Projets
                return renderProjectsDashboard();
            default:
                return null;
        }
    };

    return isLoaded ? (
        <div className="c-app__view">
            <CustomPageHeader
                title="Tableau de bord"
                titleIcon={{
                    icon: ChartBarIcon,
                }}
                lastUpdatedAt={selectedTab === 1 ? lastUpdatedAt : null}
                buttons={
                    selectedTab === 1
                        ? [
                              {
                                  label: isSyncing ? "Synchronisation en cours..." : "Synchroniser",
                                  onClick: syncAsanaData,
                                  icon: isSyncing ? null : ArrowsClockwiseIcon,
                                  disabled: isSyncing,
                                  loading: isSyncing,
                                  variant: "primary",
                              },
                              {
                                  label: "",
                                  onClick: () => setIsSimplifiedView(!isSimplifiedView),
                                  icon: isSimplifiedView ? EyeSlash : Eye,
                                  variant: "secondary",
                                  className: isSimplifiedView ? "gap-0 toggle-button active" : "gap-0 toggle-button",
                              },
                          ]
                        : []
                }
            />

            <div className="tabs-container">
                <Tabs value={selectedTab} onChange={handleTabChange} variant="fullWidth" className="custom-tabs">
                    {tabs.map((tab, index) => (
                        <Tab key={index} value={index} label={tab.label} />
                    ))}
                </Tabs>

                <div className="tab-content">{renderTabContent()}</div>
            </div>
        </div>
    ) : (
        <div className="loader-container absolute">
            <CircularProgress size={30} />
        </div>
    );
};

export default AccountingDashboard;
